/**
 * RechercherMonChantier — v13.63 Phase 3 (2026-02-28)
 *
 * Modale de secours pour les employés terrain oubliés à la cédule.
 * 3 modes de recherche :
 *   1. Par adresse (texte)
 *   2. Par nom du client (texte)
 *   3. Par proximité GPS (bouton "Utiliser ma position")
 *
 * Fenêtre backend stricte : ±7 jours. Aucune donnée financière n'est exposée.
 * L'utilisateur choisit un BT → POST `/selectionner-chantier` → le flag
 * `assignation_manuelle_employe=true` + timestamp + auteur (self) sont posés
 * côté DB. La cédule administrative + le message d'horaire déjà généré
 * restent INTACTS (exigence utilisatrice).
 *
 * Gestion gracieuse du refus GPS :
 *   • Onglet « Près de moi » affiche un message doux si permission refusée.
 *   • Les onglets Adresse/Client restent utilisables.
 *
 * Ref : /app/memory/RCA_V13_63_PHASE3_RECHERCHE_CHANTIER.md
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Search, User, Navigation, Loader2, CheckCircle2, X } from 'lucide-react';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

function ChantierCard({ chantier, onSelect, isSelecting }) {
  const dist = chantier.distance_metres;
  return (
    <div
      className="rounded-xl border-2 border-slate-200 hover:border-orange-400 bg-white p-3 shadow-sm transition-all"
      data-testid={`chantier-result-${chantier.bt_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900 truncate">
            {chantier.client_nom || chantier.dossier_nom || 'Chantier'}
          </p>
          {chantier.bt_numero && (
            <p className="text-[11px] text-slate-500 font-mono">{chantier.bt_numero}</p>
          )}
        </div>
        {chantier.deja_assignation_manuelle && (
          <Badge
            variant="outline"
            className="text-[10px] bg-violet-50 text-violet-700 border-violet-300 shrink-0"
            data-testid={`badge-deja-assigne-${chantier.bt_id}`}
          >
            Déjà sélectionné
          </Badge>
        )}
      </div>

      {(chantier.adresse_travaux || chantier.ville_travaux) && (
        <p className="text-xs text-slate-600 flex items-start gap-1 mb-1">
          <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
          <span className="min-w-0 truncate">
            {chantier.adresse_travaux || ''}
            {chantier.ville_travaux ? `, ${chantier.ville_travaux}` : ''}
            {chantier.code_postal_travaux ? `, ${chantier.code_postal_travaux}` : ''}
          </span>
        </p>
      )}

      {chantier.description_courte && (
        <p className="text-[11px] text-slate-500 italic mb-2 line-clamp-2">
          {chantier.description_courte}
        </p>
      )}

      <div className="flex items-center justify-between gap-2 mt-2">
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          {chantier.dates_chantier_fenetre?.length > 0 && (
            <span className="bg-slate-100 rounded px-1.5 py-0.5">
              {chantier.dates_chantier_fenetre.length} jour{chantier.dates_chantier_fenetre.length > 1 ? 's' : ''}
            </span>
          )}
          {typeof dist === 'number' && (
            <span className="bg-emerald-50 text-emerald-700 rounded px-1.5 py-0.5 font-medium">
              {dist < 1000 ? `${dist} m` : `${(dist / 1000).toFixed(1)} km`}
            </span>
          )}
        </div>
        <Button
          size="sm"
          className="bg-orange-600 hover:bg-orange-700 text-white text-xs h-8"
          disabled={isSelecting || chantier.deja_assignation_manuelle}
          onClick={() => onSelect(chantier)}
          data-testid={`btn-select-chantier-${chantier.bt_id}`}
        >
          {isSelecting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : chantier.deja_assignation_manuelle ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              Sélectionné
            </>
          ) : (
            <>Je travaille ici</>
          )}
        </Button>
      </div>
    </div>
  );
}

export function RechercherMonChantier({ open, onOpenChange, token, onAfterSelect }) {
  const [mode, setMode] = useState('adresse');   // adresse | client | proximite
  const [texteAdresse, setTexteAdresse] = useState('');
  const [texteClient, setTexteClient] = useState('');
  const [gpsPos, setGpsPos] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [resultats, setResultats] = useState([]);
  const [note, setNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectingId, setSelectingId] = useState(null);

  const authHeader = { Authorization: `Bearer ${token}` };

  const doTextSearch = useCallback(async (texte) => {
    const t = (texte || '').trim();
    if (t.length < 2) {
      setResultats([]);
      setNote('');
      return;
    }
    setIsLoading(true);
    setNote('');
    try {
      const r = await axios.get(`${API_URL}/api/mon-espace/rechercher-chantier`, {
        params: { texte: t, limit: 20 },
        headers: authHeader,
      });
      setResultats(r.data?.resultats || []);
      if ((r.data?.resultats || []).length === 0) {
        setNote(`Aucun chantier actif dans les 7 prochains jours ne correspond à « ${t} ».`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur lors de la recherche';
      toast.error(msg);
      setResultats([]);
    } finally { setIsLoading(false); }
  }, [token]);

  const requestGps = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setGpsError('Votre navigateur ne prend pas en charge la géolocalisation. Utilisez la recherche par adresse ou par client.');
      return;
    }
    setGpsLoading(true);
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsLoading(false);
      },
      (err) => {
        setGpsLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGpsError('Vous avez refusé la géolocalisation. Vous pouvez toujours chercher par adresse ou par nom du client.');
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setGpsError('Position indisponible. Vérifiez que le GPS est activé, puis réessayez.');
        } else if (err.code === err.TIMEOUT) {
          setGpsError('La géolocalisation a pris trop de temps. Réessayez ou utilisez la recherche par adresse.');
        } else {
          setGpsError('Impossible d\u0027obtenir votre position. Utilisez la recherche par adresse ou par client.');
        }
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 30000 },
    );
  }, []);

  const doProximiteSearch = useCallback(async () => {
    if (!gpsPos) return;
    setIsLoading(true);
    setNote('');
    try {
      const r = await axios.get(`${API_URL}/api/mon-espace/chantiers-proches`, {
        params: { lat: gpsPos.lat, lng: gpsPos.lng, radius_km: 50, limit: 20 },
        headers: authHeader,
      });
      setResultats(r.data?.resultats || []);
      if (r.data?.note) setNote(r.data.note);
      else if ((r.data?.resultats || []).length === 0) {
        setNote('Aucun chantier actif dans un rayon de 50 km. Essayez la recherche par adresse ou par nom du client.');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur lors de la recherche par proximité';
      toast.error(msg);
      setResultats([]);
    } finally { setIsLoading(false); }
  }, [gpsPos, token]);

  const handleSelect = useCallback(async (chantier) => {
    setSelectingId(chantier.bt_id);
    try {
      const r = await axios.post(
        `${API_URL}/api/mon-espace/selectionner-chantier`,
        { bt_id: chantier.bt_id },
        { headers: authHeader },
      );
      const action = r.data?.action;
      if (action === 'ajoute') {
        toast.success(
          `Vous êtes maintenant assigné à ${chantier.client_nom || 'ce chantier'}. Retour à Mon Espace…`,
        );
      } else if (action === 'deja_manuel' || action === 'deja_assigne' || action === 'race_deja_assigne') {
        toast.info('Vous étiez déjà assigné à ce chantier.');
      }
      // Refresh du widget parent, puis fermeture différée
      if (typeof onAfterSelect === 'function') { await onAfterSelect(); }
      setTimeout(() => onOpenChange?.(false), 400);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || 'Sélection impossible.';
      if (status === 400) toast.error(msg);
      else if (status === 403) toast.error('Accès refusé — permission manquante.');
      else if (status === 404) toast.error('Chantier introuvable — il a peut-être été retiré.');
      else toast.error(msg);
    } finally { setSelectingId(null); }
  }, [token, onAfterSelect, onOpenChange]);

  const handleReset = () => {
    setTexteAdresse(''); setTexteClient('');
    setGpsPos(null); setGpsError(null);
    setResultats([]); setNote('');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleReset(); onOpenChange?.(v); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-rechercher-chantier"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Search className="w-5 h-5 text-orange-600" />
            Rechercher mon chantier
          </DialogTitle>
          <DialogDescription className="text-xs">
            Solution de secours si vous n&apos;avez pas été ajouté à la cédule aujourd&apos;hui. Recherche limitée aux chantiers actifs dans les 7 prochains jours.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(v) => { setMode(v); setResultats([]); setNote(''); }}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="adresse" data-testid="tab-adresse">
              <MapPin className="w-3.5 h-3.5 mr-1" /> Adresse
            </TabsTrigger>
            <TabsTrigger value="client" data-testid="tab-client">
              <User className="w-3.5 h-3.5 mr-1" /> Client
            </TabsTrigger>
            <TabsTrigger value="proximite" data-testid="tab-proximite">
              <Navigation className="w-3.5 h-3.5 mr-1" /> Près de moi
            </TabsTrigger>
          </TabsList>

          <TabsContent value="adresse" className="space-y-3 pt-3">
            <div className="flex gap-2">
              <Input
                placeholder="Ex. Boulevard Charest, Québec, G1K…"
                value={texteAdresse}
                onChange={(e) => setTexteAdresse(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doTextSearch(texteAdresse)}
                data-testid="input-adresse"
              />
              <Button
                onClick={() => doTextSearch(texteAdresse)}
                disabled={isLoading || (texteAdresse || '').trim().length < 2}
                data-testid="btn-search-adresse"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-slate-500">Tape au moins 2 caractères. Recherche partielle : ville, rue, code postal, tout est accepté.</p>
          </TabsContent>

          <TabsContent value="client" className="space-y-3 pt-3">
            <div className="flex gap-2">
              <Input
                placeholder="Nom ou début du nom du client"
                value={texteClient}
                onChange={(e) => setTexteClient(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && doTextSearch(texteClient)}
                data-testid="input-client"
              />
              <Button
                onClick={() => doTextSearch(texteClient)}
                disabled={isLoading || (texteClient || '').trim().length < 2}
                data-testid="btn-search-client"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-slate-500">Ex. « Boucher », « Girard », « Ville de Québec »…</p>
          </TabsContent>

          <TabsContent value="proximite" className="space-y-3 pt-3">
            {!gpsPos && !gpsError && (
              <div className="space-y-2">
                <p className="text-xs text-slate-600">
                  Nous allons utiliser votre position GPS pour trouver les chantiers actifs dans un rayon de 50 km.
                </p>
                <Button
                  onClick={requestGps}
                  disabled={gpsLoading}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  data-testid="btn-request-gps"
                >
                  {gpsLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Recherche de votre position…</>
                  ) : (
                    <><Navigation className="w-4 h-4 mr-2" /> Utiliser ma position</>
                  )}
                </Button>
              </div>
            )}

            {gpsError && (
              <div
                className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2"
                data-testid="gps-error-message"
              >
                <p className="text-sm text-amber-800 font-medium">Géolocalisation impossible</p>
                <p className="text-xs text-amber-700">{gpsError}</p>
                <Button
                  variant="outline" size="sm"
                  onClick={() => { setGpsError(null); requestGps(); }}
                  data-testid="btn-retry-gps"
                >
                  Réessayer
                </Button>
              </div>
            )}

            {gpsPos && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs bg-emerald-50 border border-emerald-200 rounded p-2">
                  <span className="text-emerald-700">
                    📍 Position obtenue ({gpsPos.lat.toFixed(4)}, {gpsPos.lng.toFixed(4)})
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => { setGpsPos(null); setResultats([]); setNote(''); }}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
                <Button
                  onClick={doProximiteSearch}
                  disabled={isLoading}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  data-testid="btn-search-proximite"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Chercher les chantiers proches
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="space-y-2 pt-2" data-testid="resultats-container">
          {isLoading && (
            <div className="flex items-center justify-center py-6 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Recherche en cours…
            </div>
          )}

          {!isLoading && note && (
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2" data-testid="resultats-note">
              {note}
            </p>
          )}

          {!isLoading && resultats.length > 0 && (
            <>
              <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
                {resultats.length} chantier{resultats.length > 1 ? 's' : ''} trouvé{resultats.length > 1 ? 's' : ''}
              </p>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {resultats.map((c) => (
                  <ChantierCard
                    key={c.bt_id}
                    chantier={c}
                    isSelecting={selectingId === c.bt_id}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
